/**
 * Zod schemas for Sprint 8 — Internal Events via Transactional Outbox.
 *
 * These are the single source of truth for:
 *   - workflow_events table rows (WorkflowEventSchema)
 *   - emit-event API input (EmitEventArgsSchema)
 *   - workflow_event_subscriptions rows (WorkflowEventSubscriptionSchema)
 *   - emit_event workflow-node configuration (EmitEventNodeConfigSchema)
 *
 * All TypeScript types are derived from Zod (Rule §5.4 #26).
 */

import { z } from 'zod';
import { ExportControlSchema, SensitivitySchema } from './document.schema.js';

// ─── Status vocabulary ────────────────────────────────────────────────────────

export const WorkflowEventStatusSchema = z.enum([
  'pending',
  'dispatching',
  'dispatched',
  'failed',
  'expired',
]);
export type WorkflowEventStatus = z.infer<typeof WorkflowEventStatusSchema>;

// ─── Classification (mirrors governance classification schema) ────────────────

export const ClassificationJsonbSchema = z
  .object({
    sensitivity: SensitivitySchema
      .optional()
      .default('unclassified'),
    exportControl: ExportControlSchema.optional().default('none'),
  })
  .passthrough()
  .nullable()
  .optional();

// ─── WorkflowEventSchema — represents a row in workflow_events ───────────────

export const WorkflowEventSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  eventName: z
    .string()
    .min(1)
    .max(120)
    .regex(
      /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/,
      'Event name must be dot-separated lowercase segments (e.g. order.placed)',
    ),
  payloadJsonb: z.record(z.string(), z.unknown()).default({}),
  dedupeKey: z.string().max(255).nullable().optional(),
  classificationJsonb: ClassificationJsonbSchema,
  emittedByWorkflowRunId: z.string().uuid().nullable().optional(),
  emittedByUserId: z.string().uuid().nullable().optional(),
  status: WorkflowEventStatusSchema.default('pending'),
  dispatchAttempts: z.number().int().min(0).default(0),
  lastDispatchedAt: z.string().datetime().nullable().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  processedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
});
export type WorkflowEvent = z.infer<typeof WorkflowEventSchema>;

// ─── EmitEventArgsSchema — input for emitting an event ───────────────────────
//
// Used by:
//   - POST /v1/workflow-events (manual emit from admin rail)
//   - execEmitEvent() inside automate-runner.service.ts

export const EmitEventArgsSchema = z.object({
  organizationId: z.string().uuid(),
  eventName: z
    .string()
    .min(1)
    .max(120)
    .regex(
      /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/,
      'Event name must be dot-separated lowercase segments (e.g. order.placed)',
    ),
  payload: z.record(z.string(), z.unknown()).default({}),
  dedupeKey: z.string().max(255).optional(),
  classificationJsonb: ClassificationJsonbSchema,
  emittedByWorkflowRunId: z.string().uuid().optional(),
  emittedByUserId: z.string().uuid().optional(),
  expiresAt: z.string().datetime().optional(),
});
export type EmitEventArgs = z.infer<typeof EmitEventArgsSchema>;

// ─── WorkflowEventSubscriptionSchema — row in workflow_event_subscriptions ───

export const WorkflowEventSubscriptionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),

  /**
   * FK to workflows(id) — the unified catalog table.
   * NOT workflow_definitions.id (Data AMB-1 fix per v1.1 amendments).
   */
  workflowId: z.string().uuid(),

  eventName: z
    .string()
    .min(1)
    .max(120)
    .regex(
      /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/,
      'Event name must be dot-separated lowercase segments',
    ),

  /**
   * Optional shallow equality filter on the event payload.
   * { "status": "approved" } → only trigger if payload.status === "approved".
   */
  filterJsonb: z.record(z.string(), z.unknown()).nullable().optional(),

  /**
   * Optional JSON Schema (Draft 7) for strict payload shape validation.
   * Dispatcher validates payload before inserting automation_executions.
   * On failure → skip subscriber + emit event.payload_rejected audit.
   * Closes Security CRIT-4 from v1.1 amendments.
   */
  payloadSchemaJsonb: z
    .object({
      type: z.string().optional(),
      properties: z.record(z.string(), z.unknown()).optional(),
      required: z.array(z.string()).optional(),
    })
    .passthrough()
    .nullable()
    .optional(),

  enabled: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkflowEventSubscription = z.infer<
  typeof WorkflowEventSubscriptionSchema
>;

// ─── CreateWorkflowEventSubscriptionSchema — POST body ───────────────────────

export const CreateWorkflowEventSubscriptionSchema = z.object({
  workflowId: z.string().uuid(),
  eventName: z
    .string()
    .min(1)
    .max(120)
    .regex(
      /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/,
      'Event name must be dot-separated lowercase segments',
    ),
  filterJsonb: z.record(z.string(), z.unknown()).nullable().optional(),
  payloadSchemaJsonb: z
    .object({
      type: z.string().optional(),
      properties: z.record(z.string(), z.unknown()).optional(),
      required: z.array(z.string()).optional(),
    })
    .passthrough()
    .nullable()
    .optional(),
  enabled: z.boolean().default(true),
});
export type CreateWorkflowEventSubscription = z.infer<
  typeof CreateWorkflowEventSubscriptionSchema
>;

// ─── EmitEventNodeConfigSchema — workflow node configuration ──────────────────
//
// Stored in workflow_nodes.config for nodes with nodeKind = 'emit_event'.
// The runner resolves payload_template and dedupe_key_template against
// ctx.outputs at execution time via template variable substitution.

export const EmitEventNodeConfigSchema = z.object({
  /**
   * The event name to emit. Supports template variables: {{variable}}.
   * Must produce a valid dot-separated event name after resolution.
   */
  event_name: z
    .string()
    .min(1)
    .max(120),

  /**
   * Payload template. Keys may contain template references:
   *   { "order_id": "{{trigger.orderId}}", "status": "{{step1.status}}" }
   * Resolved against ctx.outputs before INSERT.
   */
  payload_template: z.record(z.string(), z.unknown()).default({}),

  /**
   * Optional deduplication key template.
   * E.g. "order-{{trigger.orderId}}" → deduplication on the resolved key.
   * NULL/omitted = no deduplication.
   */
  dedupe_key_template: z.string().max(255).optional(),

  /**
   * Optional TTL in seconds. Unset = event never expires.
   */
  expires_in_seconds: z.number().int().positive().optional(),
});
export type EmitEventNodeConfig = z.infer<typeof EmitEventNodeConfigSchema>;
